import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type { TeamUserWithPresence } from '@troia-v3/types';
import { useTeamMessages } from '../hooks/useTeamMessages';
import { useTeamChatSocket } from '../hooks/useTeamChatSocket';
import { TeamChatMessage } from './TeamChatMessage';
import { TeamChatInput } from './TeamChatInput';
import { Icon } from '@/shared/icons'
interface Props {
  targetUser: TeamUserWithPresence;
  roomId: string;
  panelIndex: number; // For stacking multiple panels (0 = rightmost)
  isMinimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onOpen: () => void;
}

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 580;
const PANEL_GAP = 12;
const PANEL_BOTTOM = 20;
const PANEL_RIGHT_BASE = 20;

export const TeamChatPanel: React.FC<Props> = ({
  targetUser,
  roomId,
  panelIndex,
  isMinimized,
  onClose,
  onMinimize,
  onOpen,
}) => {
  const { state: authState } = useAuth();
  const { socket } = useSocket();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useTeamMessages(targetUser.id, roomId);
  const { sendMessage, sendTyping, uploadAndSend, isTargetTyping } = useTeamChatSocket(
    targetUser.id,
    roomId
  );

  // Notify backend that messages were read when panel is opened
  useEffect(() => {
    if (socket && roomId && !isMinimized) {
      socket.emit(SOCKET_EVENTS.TEAM_MESSAGE_READ, { roomId });
    }
  }, [socket, roomId, isMinimized]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages, isMinimized]);

  const panelRight = PANEL_RIGHT_BASE + panelIndex * (PANEL_WIDTH + PANEL_GAP);
  const messages = data?.messages ?? [];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: PANEL_BOTTOM,
        right: panelRight,
        width: isMinimized ? 'fit-content' : PANEL_WIDTH,
        height: isMinimized ? 52 : PANEL_HEIGHT,
        borderRadius: 12,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 8px 40px rgba(0,0,0,0.16)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9000,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'height 0.2s ease, width 0.2s ease',
      }}
    >
      <style>{`
        .tchat-messages::-webkit-scrollbar { width: 4px; }
        .tchat-messages::-webkit-scrollbar-track { background: transparent; }
        .tchat-messages::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }
        .tchat-messages::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.25); }
        .tchat-hdr-btn:hover { background: rgba(255,255,255,0.18) !important; }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: isMinimized ? '8px 12px' : '14px 16px',
          background: 'linear-gradient(180deg, #8b5cf6 0%, #6d28d9 100%)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          cursor: isMinimized ? 'pointer' : 'default',
          flexShrink: 0,
        }}
        onClick={isMinimized ? onOpen : undefined}
      >
        {/* Avatar + presence */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {targetUser.avatar ? (
            <img
              src={targetUser.avatar}
              alt={targetUser.firstName}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid rgba(255,255,255,0.25)',
              }}
            />
          ) : (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: '#ffffff',
                border: '2px solid rgba(255,255,255,0.25)',
              }}
            >
              {targetUser.firstName[0]}{targetUser.lastName[0]}
            </div>
          )}
          <span
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: '2px solid #7c3aed',
              backgroundColor: targetUser.isOnline ? '#22c55e' : '#94a3b8',
            }}
          />
        </div>

        {/* Name + status */}
        <div style={{ flex: isMinimized ? '0 0 auto' : 1, minWidth: 0 }}>
          <p style={{
            color: '#ffffff',
            fontWeight: 600,
            fontSize: 14,
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {targetUser.firstName} {targetUser.lastName}
          </p>
          {!isMinimized && (
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, margin: 0 }}>
              {isTargetTyping ? 'Digitando...' : targetUser.isOnline ? 'Online' : 'Offline'}
            </p>
          )}
        </div>

        {/* Header action buttons */}
        <button
          className="tchat-hdr-btn"
          onClick={(e) => { e.stopPropagation(); onMinimize(); }}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.9)',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 6,
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          <Icon name="minus" size={15} />
        </button>
        <button
          className="tchat-hdr-btn"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.9)',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 6,
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          <Icon name="x" size={15} />
        </button>
      </div>

      {/* Messages area + input */}
      {!isMinimized && (
        <>
          <div
            className="tchat-messages"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 12px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              backgroundColor: '#f4f5f7',
            }}
          >
            {isLoading && (
              <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Carregando...</p>
            )}
            {!isLoading && messages.length === 0 && (
              <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 40 }}>
                Nenhuma mensagem ainda. Diga olá! 👋
              </p>
            )}
            {messages.map((msg, index) => {
              const isOwn = msg.senderId === authState.user?.id;
              const prevMsg = messages[index - 1];
              const nextMsg = messages[index + 1];
              const isFirst = !prevMsg || prevMsg.senderId !== msg.senderId;
              const isLast = !nextMsg || nextMsg.senderId !== msg.senderId;
              return (
                <TeamChatMessage
                  key={msg.id}
                  message={msg}
                  isOwn={isOwn}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              );
            })}
            {isTargetTyping && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 4, marginTop: 2 }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                  {targetUser.firstName} está digitando...
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <TeamChatInput
            onSend={sendMessage}
            onUpload={uploadAndSend}
            onTypingChange={sendTyping}
          />
        </>
      )}
    </div>
  );
};
